import { isWeb } from '@/utils/navigation';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { OrderEntryMobileScreen } from './order-entry-mobile-screen';
import { OrderEntryScreen } from './order-entry-screen';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { useEffect } from 'react';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useRedirectOperatorUsers } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-redirect-operator-users-loader';
import { validate, version } from 'uuid';

const UUID_VERSION_4 = 4;

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.OrderEntry
>;

export const OrderEntryScreenWrapper: React.FC<Props> = (props) => {
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();
  const { restaurantUUID, supplierUUID } = props.route.params;

  const areUUIDsValid =
    validate(restaurantUUID) &&
    validate(supplierUUID) &&
    version(restaurantUUID) === UUID_VERSION_4 &&
    version(supplierUUID) === UUID_VERSION_4;

  const { layoutSize } = useLayoutContext();
  const isMobileBottomNavLayout = !isWeb && layoutSize === LayoutSize.SMALL;

  const topRouteKey = useNavigationState(
    (state) => state.routes[state.index]?.key,
  );
  const isTopOfStack = topRouteKey === props.route.key;

  // Check if user is a sales rep - automatically redirects operator users to Operator Portal
  const { isSalesRep, loading: salesRepLoading } = useRedirectOperatorUsers(
    areUUIDsValid ? supplierUUID : undefined,
  );

  // Redirect if UUIDs are invalid
  useEffect(() => {
    if (!areUUIDsValid) {
      navigation.replace(SALES_PORTAL_ROUTES.NotFound);
    }
  }, [areUUIDsValid, navigation]);

  if (!areUUIDsValid || !isTopOfStack) {
    return null;
  }

  // Redirect non-sales-rep users when check has completed (onCompleted in hook handles redirect)
  if (!salesRepLoading && !isSalesRep) {
    return null;
  }

  // Always show order entry screen (with its skeleton when loading); never the full-screen logo.
  // Important: we render even when salesRepLoading is true so users see the order entry UI/skeleton
  // instead of a blank screen—do not gate on salesRepLoading here.
  if (isMobileBottomNavLayout) {
    return <OrderEntryMobileScreen {...props} />;
  }
  return <OrderEntryScreen {...props} />;
};
